import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ListPlus, Trash2, Clock, User, Building2, Calendar, StickyNote } from "lucide-react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { NoStaffNote } from "@/components/common/NoStaffNote";
import { TASK_STATUSES, StatusBadge, PriorityBadge } from "@/components/tasks/taskMeta";

const empty = {
  title: "", description: "", priority: "Medium",
  due_date: "", category: "General", progress: 0,
  assignee: "", client: "", status: "Not Started", notes: "",
};

export default function TaskModal({ open, initial, consultants = [], onOpenChange, onChanged }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) setForm({ ...empty, ...(initial || {}) });
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Task name is required");
    setSaving(true);
    const payload = { ...form, due_date: form.due_date || null };
    try {
      if (isEdit) {
        await endpoints.updateTask(initial.id, payload);
        toast.success("Task updated");
      } else {
        await endpoints.createTask(payload);
        toast.success("Task created");
      }
      onChanged?.();
    } catch {
      toast.error("Could not save task");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await endpoints.deleteTask(initial.id);
      toast.success("Task deleted");
      onChanged?.();
    } catch { toast.error("Could not delete"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-task" className="sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl brand-gradient text-white flex items-center justify-center shadow-md shadow-indigo-500/25">
              <ListPlus className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0">
              <DialogTitle>{isEdit ? "Task details" : "New task"}</DialogTitle>
              <DialogDescription>{isEdit ? "Update, reassign or move status." : "Create a task for yourself or a teammate."}</DialogDescription>
            </div>
            {isEdit && (
              <div className="ml-auto flex gap-1.5">
                <StatusBadge status={form.status} />
                <PriorityBadge priority={form.priority} />
              </div>
            )}
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3.5 pt-2">
          <div>
            <Label htmlFor="t-title">Task name</Label>
            <Input data-testid="task-title-input" id="t-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Review internal audit checklist" />
          </div>

          <div>
            <Label htmlFor="t-desc" className="flex items-center gap-1"><StickyNote className="h-3 w-3" /> Description</Label>
            <Textarea data-testid="task-desc-input" id="t-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What needs to be done?" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1"><User className="h-3 w-3" /> Assigned to</Label>
              <Select value={form.assignee || "unassigned"} onValueChange={(v) => set("assignee", v === "unassigned" ? "" : v)}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {consultants.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <NoStaffNote list={consultants} testid="task-no-staff-note" />
            </div>
            <div>
              <Label htmlFor="t-client" className="flex items-center gap-1"><Building2 className="h-3 w-3" /> Related client</Label>
              <Input data-testid="task-client-input" id="t-client" value={form.client} onChange={(e) => set("client", e.target.value)} placeholder="Optional" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="t-due" className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Due date</Label>
              <Input data-testid="task-due-input" id="t-due" type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Clock className="h-3 w-3" /> Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="t-cat">Category</Label>
              <Input data-testid="task-category-input" id="t-cat" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. ISO 9001, Documentation" />
            </div>
            <div>
              <Label>Progress · <span className="font-mono">{form.progress}%</span></Label>
              <Slider data-testid="task-progress-slider" value={[form.progress || 0]} onValueChange={([v]) => set("progress", v)} max={100} step={5} className="mt-3" />
            </div>
          </div>

          <div>
            <Label htmlFor="t-notes">Notes</Label>
            <Textarea data-testid="task-notes-input" id="t-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Extra context, blockers, decisions..." />
          </div>

          <DialogFooter className="flex-wrap gap-2">
            {isEdit && (
              <Button data-testid="task-delete-button" type="button" variant="ghost" className="text-rose-600 hover:text-rose-700 mr-auto" onClick={remove} disabled={saving}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
            <Button data-testid="task-submit-button" type="submit" disabled={saving} className="brand-gradient text-white">
              {saving ? "Saving..." : isEdit ? "Save changes" : "Create task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
